import { createReducer, on } from '@ngrx/store';
import { AssignmentRecordActions, NotesRecordActions, RundownRecordActions, StoriesRecordActions, TopicRecordActions } from '@src/suite/common/store/records/records.actions';

import { ListActions } from './list.actions';
import { ListState } from './list.models';

export const initialListState: ListState = {
    listGuids: [],
    listNewGuids: [],
    selectedNodeUuid: '',
    nodesExpandState: {},
    isLoading: false,
};

export const listReducer = createReducer(
    initialListState,
    on(ListActions.setListGuids, (state, action): ListState => {
        return {
            ...state,
            listGuids: Array.from(action.listGuids),
            isLoading: false,
        };
    }),
    on(ListActions.navigateObject, (state, { nodeUuid }): ListState => {
        return { ...state, selectedNodeUuid: nodeUuid };
    }),
    on(ListActions.changeNodeExpandedState, (state, { nodeUuid, isExpanded }): ListState => {
        return {
            ...state,
            nodesExpandState: {
                ...state.nodesExpandState,
                [nodeUuid]: isExpanded,
            },
        };
    }),
    on(ListActions.changeAllNodesExpandedState, (state, { expanderNodes, isExpanded }): ListState => {
        const newExpandState = expanderNodes.reduce((acc, node) => {
            acc[node.id] = isExpanded;
            return acc;
        }, {});
        return {
            ...state,
            nodesExpandState: newExpandState,
        };
    }),
    on(ListActions.resetNodeExpandedState, (state): ListState => {
        return {
            ...state,
            nodesExpandState: {},
        };
    }),
    on(ListActions.addListGuids, (state, { listGuids }): ListState => {
        const newListGuids = new Set([...state.listGuids, ...listGuids]);
        return {
            ...state,
            listGuids: Array.from(newListGuids),
            isLoading: false,
        };
    }),
    on(ListActions.addNewListGuids, (state, { listGuids }): ListState => {
        if (!listGuids?.length) {
            return state;
        }
        // Keep the state reference stable when nothing is actually new, so the poll tick
        // doesn't invalidate every selector downstream of the list slice every 10 seconds.
        const existingGuids = new Set(state.listNewGuids);
        if (listGuids.every(guid => existingGuids.has(guid))) {
            return state;
        }
        listGuids.forEach(guid => existingGuids.add(guid));
        return {
            ...state,
            listNewGuids: Array.from(existingGuids),
        };
    }),
    on(ListActions.removeListGuids, (state, { listGuids }): ListState => {
        if (!listGuids || listGuids.length === 0) {
            return state;
        }
        const remainingGuids = new Set(state.listGuids);
        listGuids.forEach(guid => remainingGuids.delete(guid));
        // Drop them from the new-items set as well. The badge counts these guids now (bug 110552), so a record
        // that has been removed has to stop being counted; otherwise the badge keeps reporting items that are
        // gone until the next view load, which is the only other thing that clears this list.
        const remainingNewGuids = state.listNewGuids.filter(guid => !listGuids.includes(guid));
        return {
            ...state,
            listGuids: Array.from(remainingGuids),
            listNewGuids: remainingNewGuids.length === state.listNewGuids.length ? state.listNewGuids : remainingNewGuids,
        };
    }),
    // Deleting a record has to stop it being counted, and the record actions below are the only thing every
    // delete path has in common: the local effect, the RTU effect, and the RTU dispatches in nom-web-services-json.
    on(
        TopicRecordActions.deleteTopics,
        AssignmentRecordActions.deleteAssignments,
        StoriesRecordActions.deleteStories,
        RundownRecordActions.deleteRundowns,
        NotesRecordActions.deleteNotes,
        (state, { records }): ListState => {
            const deletedGuids = ((records ?? []) as { Guid?: string }[]).map(record => record?.Guid).filter(Boolean);
            if (!deletedGuids.length) {
                return state;
            }
            const remainingNewGuids = state.listNewGuids.filter(guid => !deletedGuids.includes(guid));
            if (remainingNewGuids.length === state.listNewGuids.length) {
                return state;
            }
            return {
                ...state,
                listNewGuids: remainingNewGuids,
            };
        }
    ),
    // Covers items that disappeared without this client seeing a delete, e.g. another user deleted them.
    on(ListActions.reconcileNewListGuids, (state, { listGuids }): ListState => {
        if (!state.listNewGuids.length) {
            return state;
        }
        const presentGuids = new Set(listGuids);
        const remainingNewGuids = state.listNewGuids.filter(guid => presentGuids.has(guid));
        if (remainingNewGuids.length === state.listNewGuids.length) {
            return state;
        }
        return {
            ...state,
            listNewGuids: remainingNewGuids,
        };
    }),
    on(ListActions.clearNewListGuids, (state): ListState => {
        return {
            ...state,
            listNewGuids: [],
        };
    }),
    on(ListActions.setLoading, (state, { isLoading }): ListState => {
        return {
            ...state,
            isLoading,
        };
    })
);
